// Account-type constants + helpers. The single source of truth for
// every account-type label, color, and tax-classification fact in the
// frontend. Mirrors the backend ACCOUNT_TYPES tuple in server.py — keep
// them in lockstep when adding new types.

import { Landmark, ShieldCheck, PiggyBank, Briefcase, HeartPulse, HelpCircle } from 'lucide-react';

export const ACCOUNT_TYPES = [
  {
    key: 'taxable',
    label: 'Taxable Brokerage',
    short: 'Taxable',
    icon: Landmark,
    // NIIT applies to dividends in this account type.
    niit_applies: true,
    hint: 'Standard brokerage account. Dividends here count toward the 3.8% NIIT surtax if your MAGI is over the threshold.',
  },
  {
    key: 'roth_ira',
    label: 'Roth IRA',
    short: 'Roth IRA',
    icon: ShieldCheck,
    niit_applies: false,
    hint: 'Dividends and qualified withdrawals are fully tax-free, including exemption from the 3.8% NIIT.',
  },
  {
    key: 'traditional_ira',
    label: 'Traditional IRA',
    short: 'Trad IRA',
    icon: PiggyBank,
    niit_applies: false,
    hint: 'Dividends grow tax-deferred. Withdrawals are taxed as ordinary income later, but never face the NIIT.',
  },
  {
    key: '401k',
    label: '401(k) / 403(b)',
    short: '401(k)',
    icon: Briefcase,
    niit_applies: false,
    hint: 'Employer-sponsored retirement plan. Tax-deferred growth, no NIIT exposure.',
  },
  {
    key: 'hsa',
    label: 'HSA',
    short: 'HSA',
    icon: HeartPulse,
    niit_applies: false,
    hint: 'Health Savings Account. Triple tax-advantaged — no NIIT on dividends inside.',
  },
  {
    key: 'other',
    label: 'Other / Unspecified',
    short: 'Other',
    icon: HelpCircle,
    // Conservative: treat as taxable for NIIT purposes so we never
    // silently under-report the surtax for an unclassified lot.
    niit_applies: true,
    hint: 'Custodial, 529, or anything else. Treated like a taxable account for NIIT estimation (conservative).',
  },
];

export const DEFAULT_ACCOUNT_TYPE = 'taxable';

export const ACCOUNT_TYPE_KEYS = ACCOUNT_TYPES.map((t) => t.key);

export function getAccountType(key) {
  return ACCOUNT_TYPES.find((t) => t.key === key) || ACCOUNT_TYPES.find((t) => t.key === DEFAULT_ACCOUNT_TYPE);
}

// Tone classes for the badge / pill — keep palettes distinct so users
// can pattern-match account types at a glance on a dense portfolio page.
export const ACCOUNT_TYPE_TONE = {
  taxable:         'bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-900/60 dark:text-slate-200 dark:ring-slate-700',
  roth_ira:        'bg-emerald-50 text-emerald-700 ring-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-700/50',
  traditional_ira: 'bg-amber-50 text-amber-800 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-700/50',
  '401k':          'bg-violet-50 text-violet-700 ring-violet-300 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-700/50',
  hsa:             'bg-rose-50 text-rose-700 ring-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-700/50',
  other:           'bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-200 dark:ring-zinc-600',
};
